import { Injectable, OnDestroy } from "@angular/core";
import {
  AudioInputDeviceDisabled,
  VideoInputDeviceDisabled,
  VideoInputDeviceIsFlipped,
  VideoInputResolution
} from "@youm-front/account-device/domain";
import { DeviceFacade } from "@youm-front/device/data-access";
import {
  AudioInputDeviceId,
  AudioOutputDeviceId,
  VideoInputDeviceId
} from "@youm-front/device/domain";
import { BehaviorSubject, combineLatest, Subject } from "rxjs";
import { concatMap, filter, map, take, takeUntil, tap } from "rxjs/operators";
import { AccountDeviceService } from "../services";

@Injectable()
export class AccountDeviceFacade implements OnDestroy {
  private readonly destroy$ = new Subject<void>();
  readonly ready$ = new BehaviorSubject<boolean>(false);

  constructor(
    private readonly accountDeviceService: AccountDeviceService,
    private readonly deviceFacade: DeviceFacade
  ) {
    this.deviceFacade.devices$
      .pipe(
        concatMap((_) => this.onDevices(_)),
        takeUntil(this.destroy$)
      )
      .subscribe();

    this.deviceFacade.devices$
      .pipe(
        take(1),
        tap(() => this.ready$.next(true)),
        takeUntil(this.destroy$)
      )
      .subscribe();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();

    this.ready$.complete();
  }

  get audioInputDeviceDisabled() {
    return this.accountDeviceService.audioInputDeviceDisabled;
  }

  get audioInputDeviceDisabled$() {
    return this.accountDeviceService.audioInputDeviceDisabled$;
  }

  get audioInputDeviceId$() {
    return this.accountDeviceService.audioInputDeviceId$;
  }

  get audioOutputDeviceId$() {
    return this.accountDeviceService.audioOutputDeviceId$;
  }

  get videoInputDeviceDisabled() {
    return this.accountDeviceService.videoInputDeviceDisabled;
  }

  get videoInputDeviceDisabled$() {
    return this.accountDeviceService.videoInputDeviceDisabled$;
  }

  get videoInputDeviceId$() {
    return this.accountDeviceService.videoInputDeviceId$;
  }

  get videoInputDeviceIsFlipped$() {
    return this.accountDeviceService.videoInputDeviceIsFlipped$;
  }

  get videoInputResolution$() {
    return this.accountDeviceService.videoInputResolution$;
  }

  audioInputDeviceDisabledSet(
    audioInputDeviceDisabled: AudioInputDeviceDisabled
  ) {
    this.accountDeviceService.audioInputDeviceDisabledSet(
      audioInputDeviceDisabled
    );
  }

  audioInputDeviceIdSet(deviceId: AudioInputDeviceId) {
    this.deviceFacade.devices$
      .pipe(
        take(1),
        map((_) =>
          _.find((_) => _.deviceId === deviceId && _.kind === "audioinput")
        ),
        filter((_): _ is MediaDeviceInfo => !!_),
        tap((_) =>
          this.accountDeviceService.audioInputDeviceIdSet(
            _.deviceId as AudioInputDeviceId
          )
        )
      )
      .subscribe();
  }

  audioOutputDeviceIdSet(deviceId: AudioOutputDeviceId) {
    this.deviceFacade.devices$
      .pipe(
        take(1),
        map((_) =>
          _.find((_) => _.deviceId === deviceId && _.kind === "audiooutput")
        ),
        filter((_): _ is MediaDeviceInfo => !!_),
        tap((_) =>
          this.accountDeviceService.audioOutputDeviceIdSet(
            _.deviceId as AudioOutputDeviceId
          )
        )
      )
      .subscribe();
  }

  videoInputDeviceDisabledSet(
    videoInputDeviceDisabled: VideoInputDeviceDisabled
  ) {
    this.accountDeviceService.videoInputDeviceDisabledSet(
      videoInputDeviceDisabled
    );
  }

  videoInputDeviceIdSet(deviceId: VideoInputDeviceId) {
    this.deviceFacade.devices$
      .pipe(
        take(1),
        map((_) =>
          _.find((_) => _.deviceId === deviceId && _.kind === "videoinput")
        ),
        filter((_): _ is MediaDeviceInfo => !!_),
        tap((_) =>
          this.accountDeviceService.videoInputDeviceIdSet(
            _.deviceId as VideoInputDeviceId
          )
        )
      )
      .subscribe();
  }

  videoInputDeviceIsFlippedSet(
    videoInputDeviceIsFlipped: VideoInputDeviceIsFlipped
  ) {
    this.accountDeviceService.videoInputDeviceIsFlippedSet(
      videoInputDeviceIsFlipped
    );
  }

  videoInputResolutionSet(videoInputResolution: null | VideoInputResolution) {
    this.accountDeviceService.videoInputResolutionSet(videoInputResolution);
  }

  private onDevices(devices: MediaDeviceInfo[]) {
    return combineLatest([
      this.accountDeviceService.audioInputDeviceId$,
      this.accountDeviceService.audioOutputDeviceId$,
      this.accountDeviceService.videoInputDeviceId$
    ]).pipe(
      take(1),
      tap((_) => this.onDevices1(devices, ..._))
    );
  }

  private onDevices1(
    devices: MediaDeviceInfo[],
    audioInputDeviceId: null | AudioInputDeviceId,
    audioOutputDeviceId: null | AudioOutputDeviceId,
    videoInputDeviceId: null | VideoInputDeviceId
  ) {
    const videoInputDevices = devices.filter((_) => _.kind === "videoinput");
    const videoInputDevice =
      videoInputDevices.find((_) => _.deviceId === videoInputDeviceId) ||
      videoInputDevices[0];

    const audioInputDevices = devices.filter((_) => _.kind === "audioinput");
    const audioInputDevice =
      audioInputDevices.find((_) => _.deviceId === audioInputDeviceId) ||
      audioInputDevices[0];

    const audioOutputDevices = devices.filter((_) => _.kind === "audiooutput");
    const audioOutputDevice =
      audioOutputDevices.find((_) => _.deviceId === audioOutputDeviceId) ||
      audioOutputDevices[0];

    this.accountDeviceService.audioInputDeviceIdSet(
      audioInputDevice
        ? (audioInputDevice.deviceId as AudioInputDeviceId)
        : null
    );

    this.accountDeviceService.audioOutputDeviceIdSet(
      audioOutputDevice
        ? (audioOutputDevice.deviceId as AudioOutputDeviceId)
        : null
    );

    this.accountDeviceService.videoInputDeviceIdSet(
      videoInputDevice
        ? (videoInputDevice.deviceId as VideoInputDeviceId)
        : null
    );
  }
}
